import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Ticket, Star } from "lucide-react";
import api from "@/lib/api";

const EMPTY = { code: "", type: "percent", value: 10, scope: "all", product_id: "", max_uses: 0, expires_at: "", active: true, featured: false };

export default function AdminCoupons() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const load = () => api.get("/admin/coupons").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { ...form, value: Number(form.value), max_uses: Number(form.max_uses), product_id: form.product_id || null, expires_at: form.expires_at || null });
      toast.success("Coupon created");
      setForm({ ...EMPTY });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/admin/coupons/${id}`); load(); };
  const toggleFeatured = async (c) => {
    try {
      await api.put(`/admin/coupons/${c.id}`, {
        code: c.code, type: c.type, value: c.value, scope: c.scope,
        product_id: c.product_id || null, max_uses: c.max_uses,
        expires_at: c.expires_at || null, active: c.active, featured: !c.featured,
      });
      toast.success(c.featured ? "Removed from promo banner" : "Featured in promo banner");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const inp = "w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-3 py-2 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none";

  return (
    <div data-testid="admin-coupons">
      <div className="mb-6">
        <p className="overline mb-2">Admin · Commerce</p>
        <h1 className="font-editorial text-3xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-4xl">Coupons</h1>
        <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Percentage or fixed discounts applied at checkout. Optionally cap uses or set an expiry.</p>
      </div>

      <form onSubmit={create} className="card-moss mb-6 grid gap-3 p-5 md:grid-cols-3">
        <div><label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Code</label>
          <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="coupon-code" required className={inp} placeholder="LAUNCH20" /></div>
        <div><label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Type</label>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="coupon-type" className={inp}><option value="percent">percent (%)</option><option value="fixed">fixed ($)</option></select></div>
        <div><label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Value</label>
          <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="coupon-value" className={inp} /></div>
        <div><label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Max uses (0 = ∞)</label>
          <input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className={inp} /></div>
        <div><label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]">Expires (ISO, optional)</label>
          <input value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={inp} placeholder="2026-12-31T00:00:00Z" /></div>
        <div className="flex items-end"><button data-testid="coupon-create" className="btn-edge w-full justify-center"><Plus size={14} /> Create</button></div>
        <label className="flex items-center gap-2 text-sm text-[color:var(--ink-body)] md:col-span-3">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="coupon-featured" />
          Feature this code in the site-wide promo banner &amp; store badges
        </label>
      </form>

      <div className="grid gap-2">
        {rows.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-md border border-[color:var(--ink-hairline)] bg-white p-4">
            <div className="flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-md bg-[color:var(--evergreen-soft)] text-[color:var(--evergreen)]"><Ticket size={15} /></span>
              <div>
                <p className="font-mono-edge font-semibold text-[color:var(--ink)]">{c.code}</p>
                <p className="text-[11px] text-[color:var(--ink-muted)]">{c.type === "percent" ? `${c.value}% off` : `$${c.value} off`} · used {c.used || 0}{c.max_uses ? `/${c.max_uses}` : ""}{c.expires_at ? ` · exp ${c.expires_at.slice(0, 10)}` : ""}{c.featured ? " · featured" : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggleFeatured(c)} data-testid={`coupon-feature-${c.id}`} title={c.featured ? "Unfeature" : "Feature in promo banner"} className={`rounded-md border p-2 ${c.featured ? "border-[color:var(--evergreen)] bg-[color:var(--evergreen-soft)] text-[color:var(--evergreen)]" : "border-[color:var(--ink-hairline)] text-[color:var(--ink-muted)]"} hover:border-[color:var(--evergreen)]`}>
                <Star size={14} className={c.featured ? "fill-[color:var(--evergreen)]" : ""} />
              </button>
              <button onClick={() => remove(c.id)} data-testid={`coupon-delete-${c.id}`} className="rounded-md border border-[color:var(--ink-hairline)] p-2 text-[color:var(--alert)] hover:border-[color:var(--alert)]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="card-edge p-8 text-[color:var(--ink-muted)]">No coupons yet.</div>}
      </div>
    </div>
  );
}
